import { useState, useEffect, useCallback } from "react";
import {
  Bell,
  Send,
  Users,
  User,
  Radio,
  CheckCircle2,
  AlertTriangle,
  Lock,
  ExternalLink,
  Sparkles,
  Globe,
  Smartphone,
  Eye,
  Clock,
  RefreshCw,
  Search,
  Check,
  X,
  ChevronRight,
  ListFilter,
  BarChart3,
} from "lucide-react";
import { useStaffMe } from "../context/StaffMeContext";
import { api, ApiError } from "../api/staff";

type SenderType = "cat" | "personal";
type TargetType = "single" | "multiple" | "all";
type ChannelType = "web" | "telegram";

function StaffBadgeIcon({ size = 15, className = "" }: { size?: number; className?: string }) {
  return (
    <span title="Staff" className={`inline-flex items-center shrink-0 ${className}`}>
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 2C12 2 19 4 19 11C19 17 12 22 12 22C12 22 5 17 5 11C5 4 12 2 12 2Z" fill="#5865F2" />
        <path d="M10.87 9.25L9.66 10.46C9.27 10.85 9.27 11.48 9.66 11.87L12.13 14.34C12.52 14.73 13.15 14.73 13.54 14.34L14.75 13.13C15.14 12.74 15.14 12.11 14.75 11.72L12.28 9.25C11.89 8.86 11.26 8.86 10.87 9.25Z" fill="white" />
        <path d="M8 15L9.5 13.5L10.5 14.5L9 16L8 15Z" fill="white" />
      </svg>
    </span>
  );
}

interface NotificationHistoryItem {
  id: string;
  title: string;
  body: string;
  actionUrl?: string | null;
  actionLabel?: string | null;
  senderType: string;
  senderName: string;
  targetType: string;
  channels: string[];
  totalRecipients: number;
  webSentCount: number;
  webReadCount: number;
  tgSentCount: number;
  tgFailedCount: number;
  createdAt: string;
}

interface WebRecipient {
  recipientId: string;
  username: string | null;
  displayName: string | null;
  email: string | null;
  telegramUsername: string | null;
  readAt: string | null;
  isRead: boolean;
}

interface TgRecipient {
  userId?: string;
  chatId: string;
  username?: string | null;
  status: "sent" | "failed";
  error?: string;
}

interface NotificationDetail extends NotificationHistoryItem {
  webRecipients: WebRecipient[];
  tgRecipients: TgRecipient[];
}

export function NotificationsPage() {
  const me = useStaffMe();
  const isDev = me.role === "developer";

  // Navigation tab
  const [activeTab, setActiveTab] = useState<"compose" | "history">("compose");

  // Form State
  const [senderType, setSenderType] = useState<SenderType>("cat");
  const [targetType, setTargetType] = useState<TargetType>("single");
  const [channels, setChannels] = useState<ChannelType[]>(["web", "telegram"]);
  const [recipient, setRecipient] = useState("");
  const [recipientsText, setRecipientsText] = useState("");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [actionUrl, setActionUrl] = useState("");
  const [actionLabel, setActionLabel] = useState("");

  // Preview Mode: "web" or "telegram"
  const [previewChannel, setPreviewChannel] = useState<"web" | "telegram">("web");

  // Submission State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{
    count: number;
    webSentCount: number;
    tgSentCount: number;
    tgFailedCount: number;
    historyId?: string;
  } | null>(null);

  // History State
  const [historyList, setHistoryList] = useState<NotificationHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState<NotificationDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [recipientSearch, setRecipientSearch] = useState("");
  const [recipientFilter, setRecipientFilter] = useState<"all" | "read" | "unread">("all");

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await api<{ history: NotificationHistoryItem[] }>("/staff/notifications/history");
      setHistoryList(res.history || []);
    } catch (err) {
      console.error("Failed to load history:", err);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === "history") {
      fetchHistory();
    }
  }, [activeTab, fetchHistory]);

  const fetchDetail = async (id: string) => {
    setDetailLoading(true);
    try {
      const res = await api<NotificationDetail>(`/staff/notifications/history/${id}`);
      setSelectedDetail(res);
      setRecipientSearch("");
      setRecipientFilter("all");
    } catch (err) {
      console.error("Failed to load notification details:", err);
    } finally {
      setDetailLoading(false);
    }
  };

  const toggleChannel = (ch: ChannelType) => {
    if (channels.includes(ch)) {
      if (channels.length > 1) {
        setChannels(channels.filter((c) => c !== ch));
        if (previewChannel === ch) {
          setPreviewChannel(channels.find((c) => c !== ch)!);
        }
      }
    } else {
      setChannels([...channels, ch]);
      setPreviewChannel(ch);
    }
  };

  // Derived Sender Data for Preview
  const previewSender =
    senderType === "cat"
      ? {
          displayName: "Équipe CAT",
          username: "cat",
          avatarType: "paw",
          avatarUrl: null,
          isStaff: true,
        }
      : {
          displayName: me.displayName || me.username || "Staff",
          username: me.username || "staff",
          avatarType: me.avatarUrl ? "custom" : "paw",
          avatarUrl: me.avatarUrl,
          isStaff: true,
        };

  async function handleSend(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (channels.length === 0) {
      setError("Veuillez sélectionner au moins un canal de diffusion (Web et/ou Telegram)");
      return;
    }

    if (!title.trim()) {
      setError("Le titre de la notification est requis");
      return;
    }
    if (!body.trim()) {
      setError("Le corps du message est requis");
      return;
    }

    if (targetType === "single" && !recipient.trim()) {
      setError("Veuillez renseigner un destinataire (pseudo, e-mail ou @telegram)");
      return;
    }

    if (targetType === "multiple" && !recipientsText.trim()) {
      setError("Veuillez renseigner au moins un destinataire");
      return;
    }

    if (targetType === "all" && !isDev) {
      setError("Seuls les développeurs peuvent diffuser une notification à tous les utilisateurs");
      return;
    }

    // Confirmation if sending to all users
    if (targetType === "all") {
      const channelsLabel = channels
        .map((c) => (c === "web" ? "Plateforme Web" : "Bot Telegram"))
        .join(" et ");
      const confirmed = window.confirm(
        `ATTENTION : Cette notification sera diffusée à TOUS les utilisateurs actifs via ${channelsLabel}.\n\nConfirmez-vous l'envoi ?`
      );
      if (!confirmed) return;
    }

    setLoading(true);

    try {
      const recipientsArray =
        targetType === "multiple"
          ? recipientsText
              .split(/[\n,]+/)
              .map((s) => s.trim())
              .filter(Boolean)
          : undefined;

      const payload = {
        title: title.trim(),
        body: body.trim(),
        actionUrl: actionUrl.trim() || null,
        actionLabel: actionLabel.trim() || null,
        senderType,
        targetType,
        channels,
        recipient: targetType === "single" ? recipient.trim() : undefined,
        recipients: recipientsArray,
      };

      const res = await api<{
        success: boolean;
        count: number;
        webSentCount: number;
        tgSentCount: number;
        tgFailedCount: number;
        historyId?: string;
      }>("/staff/notifications/send", {
        method: "POST",
        body: JSON.stringify(payload),
      });

      setSuccess({
        count: res.count,
        webSentCount: res.webSentCount ?? 0,
        tgSentCount: res.tgSentCount ?? 0,
        tgFailedCount: res.tgFailedCount ?? 0,
        historyId: res.historyId,
      });

      // Reset form fields
      setTitle("");
      setBody("");
      setActionUrl("");
      setActionLabel("");
      setRecipient("");
      setRecipientsText("");
    } catch (err: any) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError(err?.message || "Une erreur est survenue lors de l'envoi");
      }
    } finally {
      setLoading(false);
    }
  }

  // Filtered Web Recipients in detail modal
  const filteredWebRecipients = (selectedDetail?.webRecipients || []).filter((u) => {
    if (recipientFilter === "read" && !u.isRead) return false;
    if (recipientFilter === "unread" && u.isRead) return false;
    if (!recipientSearch.trim()) return true;
    const query = recipientSearch.toLowerCase();
    return (
      (u.username && u.username.toLowerCase().includes(query)) ||
      (u.displayName && u.displayName.toLowerCase().includes(query)) ||
      (u.email && u.email.toLowerCase().includes(query)) ||
      (u.telegramUsername && u.telegramUsername.toLowerCase().includes(query))
    );
  });

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-border">
        <div>
          <div className="flex items-center gap-2">
            <div className="p-2 bg-primary/10 rounded-lg text-primary">
              <Bell className="w-5 h-5" />
            </div>
            <h1 className="text-xl font-bold text-foreground">Gestion & Diffusion des Notifications</h1>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Créer, diffuser et analyser l'ouverture des notifications Web et Telegram.
          </p>
        </div>

        <div className="flex items-center gap-2">
          {/* Tabs */}
          <div className="flex p-1 bg-card border border-border rounded-xl">
            <button
              onClick={() => setActiveTab("compose")}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                activeTab === "compose"
                  ? "bg-primary text-primary-foreground shadow-xs"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Send className="w-3.5 h-3.5" />
              Rédiger & Envoyer
            </button>
            <button
              onClick={() => setActiveTab("history")}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                activeTab === "history"
                  ? "bg-primary text-primary-foreground shadow-xs"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <BarChart3 className="w-3.5 h-3.5" />
              Historique & Statistiques
            </button>
          </div>

          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-card border border-border text-xs text-muted-foreground">
            <span>Rôle :</span>
            <span className="font-semibold text-foreground uppercase tracking-wider">{me.role}</span>
          </div>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
          <div className="text-sm font-medium">{error}</div>
        </div>
      )}

      {success && (
        <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5 text-emerald-500" />
            <div className="text-sm space-y-1">
              <div className="font-semibold">Notification diffusée avec succès !</div>
              <div className="text-xs text-emerald-400/80 flex items-center gap-3">
                {success.webSentCount > 0 && (
                  <span className="flex items-center gap-1">
                    <Globe className="w-3.5 h-3.5" /> {success.webSentCount} sur le Web
                  </span>
                )}
                {success.tgSentCount > 0 && (
                  <span className="flex items-center gap-1">
                    <Smartphone className="w-3.5 h-3.5" /> {success.tgSentCount} sur Telegram
                  </span>
                )}
                {success.tgFailedCount > 0 && (
                  <span className="text-amber-400">({success.tgFailedCount} échecs Telegram)</span>
                )}
              </div>
            </div>
          </div>

          {success.historyId && (
            <button
              onClick={() => {
                setActiveTab("history");
                fetchDetail(success.historyId!);
              }}
              className="px-3 py-1.5 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 text-xs font-semibold flex items-center gap-1.5 transition-colors shrink-0"
            >
              <span>Voir les stats d'ouverture</span>
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}

      {/* TAB 1: COMPOSE */}
      {activeTab === "compose" && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Form Column */}
          <form onSubmit={handleSend} className="lg:col-span-7 space-y-6">
            {/* Section 1 : Canaux de diffusion */}
            <div className="bg-card border border-border rounded-xl p-5 space-y-4">
              <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <Radio className="w-4 h-4 text-primary" />
                1. Canaux de Diffusion
              </h2>
              <p className="text-xs text-muted-foreground">
                Choisissez où la notification sera distribuée. Vous pouvez cocher un ou plusieurs canaux.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                {/* Web Toggle */}
                <div
                  onClick={() => toggleChannel("web")}
                  className={`flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition-all ${
                    channels.includes("web")
                      ? "border-primary bg-primary/5 text-foreground ring-1 ring-primary/20"
                      : "border-border bg-card/50 text-muted-foreground hover:border-border/80"
                  }`}
                >
                  <div
                    className={`w-5 h-5 rounded-md flex items-center justify-center mt-0.5 transition-colors ${
                      channels.includes("web") ? "bg-primary text-primary-foreground" : "border border-border"
                    }`}
                  >
                    {channels.includes("web") && <Check className="w-3.5 h-3.5" />}
                  </div>
                  <div className="space-y-0.5">
                    <div className="text-xs font-semibold flex items-center gap-1.5">
                      <Globe className="w-4 h-4 text-blue-500" />
                      <span>Plateforme Web</span>
                    </div>
                    <p className="text-[11px] text-muted-foreground">
                      Notification cloche dans l'application avec suivi d'ouverture précis.
                    </p>
                  </div>
                </div>

                {/* Telegram Toggle */}
                <div
                  onClick={() => toggleChannel("telegram")}
                  className={`flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition-all ${
                    channels.includes("telegram")
                      ? "border-primary bg-primary/5 text-foreground ring-1 ring-primary/20"
                      : "border-border bg-card/50 text-muted-foreground hover:border-border/80"
                  }`}
                >
                  <div
                    className={`w-5 h-5 rounded-md flex items-center justify-center mt-0.5 transition-colors ${
                      channels.includes("telegram") ? "bg-primary text-primary-foreground" : "border border-border"
                    }`}
                  >
                    {channels.includes("telegram") && <Check className="w-3.5 h-3.5" />}
                  </div>
                  <div className="space-y-0.5">
                    <div className="text-xs font-semibold flex items-center gap-1.5">
                      <Smartphone className="w-4 h-4 text-sky-400" />
                      <span>Bot Telegram</span>
                    </div>
                    <p className="text-[11px] text-muted-foreground">
                      Message privé direct expédié au nom officiel de Collect & Track.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 2 : Expéditeur */}
            <div className="bg-card border border-border rounded-xl p-5 space-y-4">
              <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <Radio className="w-4 h-4 text-primary" />
                2. Expéditeur (Anonymat)
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label
                  className={`flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition-all ${
                    senderType === "cat"
                      ? "border-primary bg-primary/5 text-foreground ring-1 ring-primary/20"
                      : "border-border bg-card/50 text-muted-foreground hover:border-border/80"
                  }`}
                >
                  <input
                    type="radio"
                    name="senderType"
                    value="cat"
                    checked={senderType === "cat"}
                    onChange={() => setSenderType("cat")}
                    className="mt-1"
                  />
                  <div className="space-y-1">
                    <div className="text-xs font-semibold flex items-center gap-1.5 text-foreground">
                      <span>Équipe CAT (Officiel)</span>
                      <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                    </div>
                    <p className="text-[11px] text-muted-foreground">
                      Recommandé. Présenté comme le compte officiel. Ne dévoile jamais votre identité personnelle.
                    </p>
                  </div>
                </label>

                <label
                  className={`flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition-all ${
                    senderType === "personal"
                      ? "border-primary bg-primary/5 text-foreground ring-1 ring-primary/20"
                      : "border-border bg-card/50 text-muted-foreground hover:border-border/80"
                  }`}
                >
                  <input
                    type="radio"
                    name="senderType"
                    value="personal"
                    checked={senderType === "personal"}
                    onChange={() => setSenderType("personal")}
                    className="mt-1"
                  />
                  <div className="space-y-1">
                    <div className="text-xs font-semibold flex items-center gap-1.5 text-foreground">
                      <span>Profil Personnel Staff</span>
                      <StaffBadgeIcon size={14} />
                    </div>
                    <p className="text-[11px] text-muted-foreground">
                      Envoyé avec votre nom ({me.displayName || me.username || "Staff"}). (Sur Telegram, le bot officiel enverra toujours le message).
                    </p>
                  </div>
                </label>
              </div>
            </div>

            {/* Section 3 : Destinataires */}
            <div className="bg-card border border-border rounded-xl p-5 space-y-4">
              <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <Users className="w-4 h-4 text-primary" />
                3. Destinataires
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                <button
                  type="button"
                  onClick={() => setTargetType("single")}
                  className={`flex flex-col items-center justify-center p-3 rounded-xl border text-center transition-all ${
                    targetType === "single"
                      ? "border-primary bg-primary/10 text-primary font-semibold shadow-xs"
                      : "border-border bg-card/50 text-muted-foreground hover:bg-accent hover:text-foreground"
                  }`}
                >
                  <User className="w-4 h-4 mb-1.5" />
                  <span className="text-xs">Utilisateur unique</span>
                </button>

                <button
                  type="button"
                  onClick={() => setTargetType("multiple")}
                  className={`flex flex-col items-center justify-center p-3 rounded-xl border text-center transition-all ${
                    targetType === "multiple"
                      ? "border-primary bg-primary/10 text-primary font-semibold shadow-xs"
                      : "border-border bg-card/50 text-muted-foreground hover:bg-accent hover:text-foreground"
                  }`}
                >
                  <Users className="w-4 h-4 mb-1.5" />
                  <span className="text-xs">Sélection multiple</span>
                </button>

                <button
                  type="button"
                  onClick={() => isDev && setTargetType("all")}
                  disabled={!isDev}
                  className={`flex flex-col items-center justify-center p-3 rounded-xl border text-center transition-all relative ${
                    targetType === "all"
                      ? "border-primary bg-primary/10 text-primary font-semibold shadow-xs"
                      : "border-border bg-card/50 text-muted-foreground hover:bg-accent hover:text-foreground"
                  } ${!isDev ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  <Radio className="w-4 h-4 mb-1.5" />
                  <span className="text-xs">Tous les utilisateurs</span>
                  {!isDev && (
                    <span className="absolute top-1 right-1 p-0.5 rounded-full bg-border text-muted-foreground">
                      <Lock className="w-2.5 h-2.5" />
                    </span>
                  )}
                </button>
              </div>

              {targetType === "single" && (
                <div className="space-y-1.5 pt-1">
                  <label className="text-xs font-medium text-foreground">
                    Destinataire (Pseudo Web, E-mail, UUID, ou @username Telegram)
                  </label>
                  <input
                    type="text"
                    value={recipient}
                    onChange={(e) => setRecipient(e.target.value)}
                    placeholder="ex: @ilyess, user@example.com ou 12345678"
                    className="w-full px-3.5 py-2 text-xs rounded-xl bg-background border border-border focus:border-primary focus:outline-none text-foreground placeholder:text-muted-foreground font-mono"
                  />
                </div>
              )}

              {targetType === "multiple" && (
                <div className="space-y-1.5 pt-1">
                  <label className="text-xs font-medium text-foreground">
                    Liste des destinataires (séparés par des virgules ou retours à la ligne)
                  </label>
                  <textarea
                    rows={3}
                    value={recipientsText}
                    onChange={(e) => setRecipientsText(e.target.value)}
                    placeholder="@user1, @user2&#10;user3@domain.com&#10;@telegram_handle"
                    className="w-full px-3.5 py-2 text-xs rounded-xl bg-background border border-border focus:border-primary focus:outline-none text-foreground placeholder:text-muted-foreground font-mono"
                  />
                </div>
              )}

              {targetType === "all" && (
                <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-500 text-xs flex items-start gap-2.5">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <div>
                    <strong>Attention :</strong> Ce message sera diffusé à l'ensemble des utilisateurs actifs de la plateforme sur les canaux sélectionnés ({channels.join(" & ")}).
                  </div>
                </div>
              )}
            </div>

            {/* Section 4 : Message */}
            <div className="bg-card border border-border rounded-xl p-5 space-y-4">
              <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <Bell className="w-4 h-4 text-primary" />
                4. Contenu de la Notification
              </h2>

              <div className="space-y-3.5">
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-medium text-foreground">Titre de la notification *</label>
                    <span className="text-[10px] text-muted-foreground">{title.length} / 120</span>
                  </div>
                  <input
                    type="text"
                    maxLength={120}
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="ex: 🚀 Nouveauté : Recherche inversée disponible"
                    className="w-full px-3.5 py-2 text-xs rounded-xl bg-background border border-border focus:border-primary focus:outline-none text-foreground placeholder:text-muted-foreground"
                  />
                </div>

                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-medium text-foreground">Corps du message *</label>
                    <span className="text-[10px] text-muted-foreground">{body.length} / 2000</span>
                  </div>
                  <textarea
                    rows={5}
                    maxLength={2000}
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    placeholder="Rédigez le message à destination de vos utilisateurs..."
                    className="w-full px-3.5 py-2 text-xs rounded-xl bg-background border border-border focus:border-primary focus:outline-none text-foreground placeholder:text-muted-foreground leading-relaxed"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-foreground flex items-center gap-1.5">
                      <ExternalLink className="w-3.5 h-3.5 text-muted-foreground" />
                      Lien d'action (Optionnel)
                    </label>
                    <input
                      type="url"
                      value={actionUrl}
                      onChange={(e) => setActionUrl(e.target.value)}
                      placeholder="https://app.collectandtrack.lol/..."
                      className="w-full px-3.5 py-2 text-xs rounded-xl bg-background border border-border focus:border-primary focus:outline-none text-foreground placeholder:text-muted-foreground font-mono"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-foreground">Libellé du bouton</label>
                    <input
                      type="text"
                      maxLength={100}
                      value={actionLabel}
                      onChange={(e) => setActionLabel(e.target.value)}
                      placeholder="ex: Voir les détails →"
                      className="w-full px-3.5 py-2 text-xs rounded-xl bg-background border border-border focus:border-primary focus:outline-none text-foreground placeholder:text-muted-foreground"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-4 rounded-xl bg-primary text-primary-foreground font-semibold text-xs flex items-center justify-center gap-2 hover:bg-primary/90 transition-all shadow-md disabled:opacity-50 disabled:pointer-events-none active:scale-[0.99]"
            >
              {loading ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span>Diffusion en cours sur les canaux sélectionnés...</span>
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  <span>
                    Diffuser la notification (
                    {channels.map((c) => (c === "web" ? "Web" : "Telegram")).join(" + ")}
                    )
                  </span>
                </>
              )}
            </button>
          </form>

          {/* Live Preview Column */}
          <div className="lg:col-span-5 space-y-4">
            <div className="flex items-center justify-between pb-1">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Aperçu en Direct
              </h2>
              <div className="flex items-center gap-1 p-0.5 rounded-lg bg-card border border-border">
                <button
                  type="button"
                  onClick={() => setPreviewChannel("web")}
                  className={`px-2.5 py-1 rounded-md text-[11px] font-medium flex items-center gap-1.5 transition-colors ${
                    previewChannel === "web"
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Globe className="w-3 h-3" />
                  Web
                </button>
                <button
                  type="button"
                  onClick={() => setPreviewChannel("telegram")}
                  className={`px-2.5 py-1 rounded-md text-[11px] font-medium flex items-center gap-1.5 transition-colors ${
                    previewChannel === "telegram"
                      ? "bg-sky-500 text-white"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Smartphone className="w-3 h-3" />
                  Telegram
                </button>
              </div>
            </div>

            {/* PREVIEW: WEB */}
            {previewChannel === "web" && (
              <div className="rounded-2xl border border-white/10 bg-[#121316] p-5 shadow-2xl space-y-4 text-left">
                <div className="flex items-center justify-between pb-3 border-b border-white/5">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                    <span className="text-xs font-medium text-neutral-300">Rendu Cloche Web</span>
                  </div>
                  <span className="text-[10px] text-neutral-500 font-mono">Collect & Track Client</span>
                </div>

                <div className="p-3.5 rounded-xl bg-white/[0.03] border border-white/[0.08] flex items-start gap-3">
                  <div className="shrink-0">
                    {previewSender.avatarType === "custom" && previewSender.avatarUrl ? (
                      <img
                        src={previewSender.avatarUrl}
                        alt="Avatar"
                        className="w-8 h-8 rounded-full object-cover ring-1 ring-white/10"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center p-1.5 shadow-sm">
                        <img src="/logo-dark.svg" alt="CAT" className="w-full h-full object-contain" />
                      </div>
                    )}
                  </div>

                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-[11px] font-semibold text-neutral-100">
                        {previewSender.displayName}
                      </span>
                      {previewSender.isStaff && <StaffBadgeIcon size={14} className="-mt-0.5" />}
                      <span className="text-[10px] text-neutral-400 font-mono">@{previewSender.username}</span>
                      <span className="ml-auto text-[9px] text-neutral-500">à l'instant</span>
                    </div>

                    <p className="text-[11px] font-medium text-[#fbe7b5]/90 truncate">
                      {title || "Titre de la notification..."}
                    </p>
                    <p className="text-[10px] text-neutral-400 line-clamp-2 leading-relaxed">
                      {body || "Le message apparaîtra ici..."}
                    </p>
                  </div>
                </div>

                {/* Detail Simulation */}
                <div className="pt-2 border-t border-white/5 space-y-3">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-neutral-400">
                    Aperçu dans la page de détail :
                  </div>

                  <div className="p-3.5 rounded-xl bg-white/[0.02] border border-white/[0.06] space-y-3">
                    <div className="space-y-1.5">
                      <h3 className="text-xs font-semibold text-[#fbe7b5]">
                        {title || "Titre de la notification"}
                      </h3>
                      <p className="text-[11px] text-neutral-300 whitespace-pre-wrap leading-relaxed">
                        {body || "Contenu complet du message rédigé..."}
                      </p>

                      {(actionUrl || actionLabel) && (
                        <div className="pt-2">
                          <span className="inline-flex items-center gap-1.5 rounded-lg border border-[#fbe7b5]/20 bg-[#fbe7b5]/10 px-3 py-1.5 text-[11px] font-semibold text-[#fbe7b5]">
                            {actionLabel || "En savoir plus"} →
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* PREVIEW: TELEGRAM */}
            {previewChannel === "telegram" && (
              <div className="rounded-2xl border border-sky-500/20 bg-[#0f141c] p-5 shadow-2xl space-y-4 text-left">
                <div className="flex items-center justify-between pb-3 border-b border-white/5">
                  <div className="flex items-center gap-2">
                    <Smartphone className="w-4 h-4 text-sky-400" />
                    <span className="text-xs font-medium text-sky-300">Message Telegram Officiel</span>
                  </div>
                  <span className="text-[10px] text-neutral-500 font-mono">@CollectAndTrackBot</span>
                </div>

                {/* Telegram Bubble */}
                <div className="p-4 rounded-2xl rounded-tl-sm bg-[#1c2430] border border-sky-500/20 shadow-lg space-y-3">
                  <div className="flex items-center gap-2 text-xs font-bold text-sky-400 border-b border-white/5 pb-2">
                    <Sparkles className="w-4 h-4 text-amber-400 shrink-0" />
                    <span>📢 {title || "Titre de la notification"}</span>
                  </div>

                  <p className="text-xs text-neutral-200 whitespace-pre-wrap leading-relaxed">
                    {body || "Le texte de la notification sera envoyé directement aux utilisateurs Telegram..."}
                  </p>

                  <div className="text-[10px] text-neutral-400 text-right pt-1">
                    {new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} ✓✓
                  </div>
                </div>

                {/* Telegram Inline Button */}
                {(actionUrl || actionLabel) && (
                  <div className="pt-1">
                    <div className="w-full py-2.5 px-4 rounded-xl bg-sky-500/15 border border-sky-500/30 text-sky-300 text-xs font-semibold text-center flex items-center justify-center gap-2">
                      <ExternalLink className="w-3.5 h-3.5" />
                      <span>{actionLabel || "🔗 Ouvrir le lien"}</span>
                    </div>
                  </div>
                )}

                <p className="text-[11px] text-neutral-500 text-center italic">
                  * Expédié anonymement par le bot officiel sans mention du profil staff.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* TAB 2: HISTORY & STATS */}
      {activeTab === "history" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-base font-bold text-foreground">Historique des Diffusions</h2>
              <p className="text-xs text-muted-foreground">
                Consultez les statistiques d'ouverture et découvrez qui a lu chaque notification.
              </p>
            </div>

            <button
              onClick={fetchHistory}
              disabled={historyLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium border bg-card border-border text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${historyLoading ? "animate-spin" : ""}`} />
              Actualiser
            </button>
          </div>

          {historyLoading && historyList.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground text-xs flex flex-col items-center gap-2">
              <RefreshCw className="w-6 h-6 animate-spin text-primary" />
              <span>Chargement de l'historique...</span>
            </div>
          ) : historyList.length === 0 ? (
            <div className="p-12 text-center rounded-2xl bg-card border border-border text-muted-foreground text-xs space-y-2">
              <Bell className="w-8 h-8 mx-auto text-muted-foreground/40" />
              <p className="font-semibold text-foreground">Aucune notification diffusée pour le moment.</p>
              <p>Envoyez votre première notification depuis l'onglet Rédiger & Envoyer.</p>
            </div>
          ) : (
            <div className="rounded-2xl border border-border bg-card overflow-hidden shadow-xs">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-muted/50 border-b border-border text-muted-foreground font-semibold">
                    <tr>
                      <th className="py-3 px-4">Date</th>
                      <th className="py-3 px-4">Titre & Message</th>
                      <th className="py-3 px-4">Expéditeur</th>
                      <th className="py-3 px-4">Canaux</th>
                      <th className="py-3 px-4">Cible</th>
                      <th className="py-3 px-4 text-center">Taux d'ouverture Web</th>
                      <th className="py-3 px-4 text-center">Telegram</th>
                      <th className="py-3 px-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {historyList.map((item) => {
                      const openRate =
                        item.webSentCount > 0
                          ? Math.round((item.webReadCount / item.webSentCount) * 100)
                          : null;

                      return (
                        <tr key={item.id} className="hover:bg-accent/40 transition-colors">
                          <td className="py-3.5 px-4 text-muted-foreground whitespace-nowrap">
                            <div className="flex items-center gap-1.5 font-medium text-foreground">
                              <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                              {new Date(item.createdAt).toLocaleDateString("fr-FR", {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                              })}
                            </div>
                            <div className="text-[11px] text-muted-foreground font-mono">
                              {new Date(item.createdAt).toLocaleTimeString("fr-FR", {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </div>
                          </td>

                          <td className="py-3.5 px-4 max-w-xs">
                            <div className="font-semibold text-foreground truncate">{item.title}</div>
                            <div className="text-[11px] text-muted-foreground truncate">{item.body}</div>
                          </td>

                          <td className="py-3.5 px-4 whitespace-nowrap font-medium text-foreground">
                            {item.senderName}
                          </td>

                          <td className="py-3.5 px-4 whitespace-nowrap">
                            <div className="flex items-center gap-1.5">
                              {item.channels.includes("web") && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-500 font-medium text-[10px] border border-blue-500/20">
                                  <Globe className="w-3 h-3" /> Web
                                </span>
                              )}
                              {item.channels.includes("telegram") && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-sky-500/10 text-sky-400 font-medium text-[10px] border border-sky-500/20">
                                  <Smartphone className="w-3 h-3" /> Telegram
                                </span>
                              )}
                            </div>
                          </td>

                          <td className="py-3.5 px-4 whitespace-nowrap text-muted-foreground">
                            {item.targetType === "all" ? (
                              <span className="font-medium text-foreground">Tous ({item.totalRecipients})</span>
                            ) : item.targetType === "single" ? (
                              <span>1 destinataire</span>
                            ) : (
                              <span>Multiple ({item.totalRecipients})</span>
                            )}
                          </td>

                          {/* Open Rate Web */}
                          <td className="py-3.5 px-4 text-center whitespace-nowrap">
                            {item.webSentCount > 0 ? (
                              <div className="inline-flex flex-col items-center gap-1">
                                <span
                                  className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${
                                    openRate! >= 50
                                      ? "bg-emerald-500/10 text-emerald-500 border border-emerald-500/20"
                                      : "bg-amber-500/10 text-amber-500 border border-amber-500/20"
                                  }`}
                                >
                                  {openRate}% ({item.webReadCount}/{item.webSentCount})
                                </span>
                              </div>
                            ) : (
                              <span className="text-muted-foreground/40">—</span>
                            )}
                          </td>

                          {/* Telegram stats */}
                          <td className="py-3.5 px-4 text-center whitespace-nowrap">
                            {item.tgSentCount > 0 ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 text-[11px] font-semibold border border-emerald-500/20">
                                <Check className="w-3 h-3" /> {item.tgSentCount} délivré(s)
                              </span>
                            ) : item.tgFailedCount > 0 ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-500/10 text-rose-400 text-[11px] font-semibold border border-rose-500/20">
                                <X className="w-3 h-3" /> {item.tgFailedCount} échec(s)
                              </span>
                            ) : (
                              <span className="text-muted-foreground/40">—</span>
                            )}
                          </td>

                          <td className="py-3.5 px-4 text-right whitespace-nowrap">
                            <button
                              onClick={() => fetchDetail(item.id)}
                              className="px-3 py-1.5 rounded-lg bg-primary/10 hover:bg-primary/20 text-primary text-xs font-semibold inline-flex items-center gap-1 transition-colors"
                            >
                              <Eye className="w-3.5 h-3.5" />
                              <span>Qui a ouvert</span>
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* DETAIL MODAL / DRAWER */}
      {selectedDetail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-md animate-in fade-in duration-200">
          <div className="relative w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-2xl border border-border bg-card shadow-2xl flex flex-col">
            {/* Modal Header */}
            <div className="p-5 border-b border-border flex items-start justify-between gap-4 bg-muted/30">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-bold text-foreground">{selectedDetail.title}</h3>
                  <div className="flex items-center gap-1">
                    {selectedDetail.channels.includes("web") && (
                      <span className="px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-500 text-[10px] font-bold border border-blue-500/20">
                        Web
                      </span>
                    )}
                    {selectedDetail.channels.includes("telegram") && (
                      <span className="px-2 py-0.5 rounded-full bg-sky-500/10 text-sky-400 text-[10px] font-bold border border-sky-500/20">
                        Telegram
                      </span>
                    )}
                  </div>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed whitespace-pre-wrap max-h-20 overflow-y-auto">
                  {selectedDetail.body}
                </p>
              </div>

              <button
                onClick={() => setSelectedDetail(null)}
                className="p-1.5 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Metrics Bar */}
            <div className="p-5 border-b border-border grid grid-cols-2 sm:grid-cols-4 gap-3 bg-card">
              <div className="p-3 rounded-xl bg-background border border-border">
                <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider block">
                  Envoyés sur le Web
                </span>
                <span className="text-xl font-bold text-foreground mt-0.5 block">
                  {selectedDetail.webSentCount}
                </span>
              </div>

              <div className="p-3 rounded-xl bg-background border border-border">
                <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider block">
                  Lus / Ouverts
                </span>
                <span className="text-xl font-bold text-emerald-500 mt-0.5 block">
                  {selectedDetail.webReadCount}
                </span>
              </div>

              <div className="p-3 rounded-xl bg-background border border-border">
                <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider block">
                  Taux de Lecture Web
                </span>
                <span className="text-xl font-bold text-primary mt-0.5 block">
                  {selectedDetail.webSentCount > 0
                    ? `${Math.round((selectedDetail.webReadCount / selectedDetail.webSentCount) * 100)}%`
                    : "—"}
                </span>
              </div>

              <div className="p-3 rounded-xl bg-background border border-border">
                <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider block">
                  Délivrés sur Telegram
                </span>
                <span className="text-xl font-bold text-sky-400 mt-0.5 block">
                  {selectedDetail.tgSentCount}
                  {selectedDetail.tgFailedCount > 0 && (
                    <span className="text-xs font-normal text-rose-400 ml-1.5">
                      ({selectedDetail.tgFailedCount} échec)
                    </span>
                  )}
                </span>
              </div>
            </div>

            {/* Modal Content - Recipients List */}
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <h4 className="text-sm font-bold text-foreground flex items-center gap-2">
                  <Users className="w-4 h-4 text-primary" />
                  Liste des Destinataires Web ({selectedDetail.webRecipients.length})
                </h4>

                <div className="flex items-center gap-2">
                  {/* Filter Pills */}
                  <div className="flex p-0.5 bg-background border border-border rounded-lg text-xs">
                    <button
                      onClick={() => setRecipientFilter("all")}
                      className={`px-2.5 py-1 rounded-md font-medium transition-colors ${
                        recipientFilter === "all" ? "bg-primary text-primary-foreground font-semibold" : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      Tous
                    </button>
                    <button
                      onClick={() => setRecipientFilter("read")}
                      className={`px-2.5 py-1 rounded-md font-medium transition-colors ${
                        recipientFilter === "read" ? "bg-emerald-500 text-white font-semibold" : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      Lus ({selectedDetail.webReadCount})
                    </button>
                    <button
                      onClick={() => setRecipientFilter("unread")}
                      className={`px-2.5 py-1 rounded-md font-medium transition-colors ${
                        recipientFilter === "unread" ? "bg-amber-500 text-white font-semibold" : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      Non lus ({selectedDetail.webSentCount - selectedDetail.webReadCount})
                    </button>
                  </div>

                  {/* Search */}
                  <div className="relative">
                    <Search className="w-3.5 h-3.5 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      value={recipientSearch}
                      onChange={(e) => setRecipientSearch(e.target.value)}
                      placeholder="Filtrer pseudo, email..."
                      className="pl-8 pr-3 py-1.5 text-xs rounded-lg bg-background border border-border focus:border-primary focus:outline-none text-foreground placeholder:text-muted-foreground"
                    />
                  </div>
                </div>
              </div>

              {filteredWebRecipients.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground text-xs">
                  Aucun destinataire ne correspond aux critères de filtre.
                </div>
              ) : (
                <div className="rounded-xl border border-border overflow-hidden">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-muted/50 border-b border-border text-muted-foreground font-semibold">
                      <tr>
                        <th className="py-2.5 px-3">Utilisateur</th>
                        <th className="py-2.5 px-3">Email</th>
                        <th className="py-2.5 px-3">Compte Telegram</th>
                        <th className="py-2.5 px-3 text-right">Statut de Lecture</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {filteredWebRecipients.map((u) => (
                        <tr key={u.recipientId} className="hover:bg-accent/40 transition-colors">
                          <td className="py-2.5 px-3 font-semibold text-foreground">
                            {u.username ? `@${u.username}` : u.displayName || "Utilisateur"}
                          </td>
                          <td className="py-2.5 px-3 text-muted-foreground font-mono text-[11px]">
                            {u.email || "—"}
                          </td>
                          <td className="py-2.5 px-3 text-muted-foreground">
                            {u.telegramUsername ? (
                              <span className="text-sky-400 font-mono">@{u.telegramUsername}</span>
                            ) : (
                              <span className="text-muted-foreground/40">—</span>
                            )}
                          </td>
                          <td className="py-2.5 px-3 text-right">
                            {u.isRead ? (
                              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-500 font-bold text-[10px] border border-emerald-500/20">
                                <Check className="w-3 h-3" /> Lu le{" "}
                                {new Date(u.readAt!).toLocaleDateString("fr-FR", {
                                  day: "2-digit",
                                  month: "2-digit",
                                })} à {new Date(u.readAt!).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted text-muted-foreground font-medium text-[10px]">
                                ⚪ Non lu
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Telegram Recipients Section */}
              {selectedDetail.tgRecipients.length > 0 && (
                <div className="pt-4 border-t border-border space-y-3">
                  <h4 className="text-sm font-bold text-foreground flex items-center gap-2">
                    <Smartphone className="w-4 h-4 text-sky-400" />
                    Destinataires Telegram ({selectedDetail.tgRecipients.length})
                  </h4>

                  <div className="rounded-xl border border-border overflow-hidden">
                    <table className="w-full text-left text-xs">
                      <thead className="bg-muted/50 border-b border-border text-muted-foreground font-semibold">
                        <tr>
                          <th className="py-2.5 px-3">Compte Telegram</th>
                          <th className="py-2.5 px-3">Chat ID</th>
                          <th className="py-2.5 px-3 text-right">Statut Telegram</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {selectedDetail.tgRecipients.map((tg, idx) => (
                          <tr key={idx} className="hover:bg-accent/40 transition-colors">
                            <td className="py-2.5 px-3 font-semibold text-foreground">
                              {tg.username ? `@${tg.username}` : "Compte lié"}
                            </td>
                            <td className="py-2.5 px-3 text-muted-foreground font-mono text-[11px]">
                              {tg.chatId}
                            </td>
                            <td className="py-2.5 px-3 text-right">
                              {tg.status === "sent" ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 font-bold text-[10px] border border-emerald-500/20">
                                  <Check className="w-3 h-3" /> Délivré avec succès
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-500/10 text-rose-400 font-medium text-[10px] border border-rose-500/20" title={tg.error}>
                                  <X className="w-3 h-3" /> {tg.error || "Échec"}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
